import _get from 'lodash/get';
import _merge from 'lodash/merge';
import _isNumber from 'lodash/isNumber';
import config from '../../config/app.config';
import { AggregationGroupsScheme } from './schemes/AggregationScheme';
import { DEALER_ADMIN } from './constants/profile';
import { i18nextInstance } from '../../i18n';
import { formatTimestamp, isDateInDaysPeriod } from '@bytebrand/fe-ui-core-autobahn/utils';
import { DEFAULT_LANG, MONTHS } from './constants/app';
import { getComputedFinancing, normalizeRateObject } from '@bytebrand/fe-financing';
import FinancingStore from '../stores/FinancingStore';
const daysPeriod = 7;
// ADV-166: "Neu" badge — modificationDate within daysPeriod is no longer enough on its
// own (it kept flagging cars that were merely re-crawled/re-priced as "new"). Now also
// requires a near-zero mileage AND a recent first registration (or none at all — cars
// with no known first registration can't fail an age check that has no data to run on).
const NEW_LABEL_MAX_MILEAGE_KM = 1000;
const NEW_LABEL_MAX_AGE_MONTHS = 12;
const AVG_MS_PER_MONTH = 1000 * 60 * 60 * 24 * 30.44;
const isNewLabelAgeEligible = (firstRegistration) => {
    if (!firstRegistration)
        return true; // N/A first registration -> age check can't disqualify it
    const registeredAtMs = firstRegistration instanceof Date ? firstRegistration.getTime() : Number(firstRegistration) * 1000;
    const ageMonths = (Date.now() - registeredAtMs) / AVG_MS_PER_MONTH;
    return ageMonths < NEW_LABEL_MAX_AGE_MONTHS;
};
const shouldShowNewLabel = (modificationDate, mileageKm, firstRegistration) => isDateInDaysPeriod(modificationDate, daysPeriod) &&
    Number(mileageKm) < NEW_LABEL_MAX_MILEAGE_KM &&
    isNewLabelAgeEligible(firstRegistration);
export const transformCarImages = (imagesData, carID) => {
    const { images } = imagesData;
    const imagesTitles = Object.keys(images);
    const transformedImages = [];
    for (let i = 0; i < imagesTitles.length; i += 1) {
        const imgObj = images[imagesTitles[i]];
        const transformedImage = {
            url: `${config.imagesAPI.imgUrl}/${carID}/${imgObj.imageIdentifier}`,
            description: `${imgObj.added}`,
            metadata: imgObj.metadata
        };
        if (transformedImage.metadata['ext_f_right'] === '1') {
            transformedImages.unshift(transformedImage);
        }
        else {
            transformedImages.push(transformedImage);
        }
    }
    return transformedImages;
};
export const transformAggregation = (aggregation) => {
    const aggKeys = Object.keys(aggregation);
    const agg = {};
    for (let i = 0; i < aggKeys.length; i += 1) {
        const itemKey = aggKeys[i];
        const itemValue = aggregation[itemKey];
        const itemGroup = AggregationGroupsScheme[itemKey];
        if (itemGroup) {
            if (!agg[itemGroup]) {
                agg[itemGroup] = {};
            }
            agg[itemGroup][itemKey] = itemValue;
        }
    }
    return agg;
};
export const getComparableVehicle = (car, _carDiff) => {
    const { getUserCarAdjustments, getUserConfig, hasUserConfig, normalizeRateObject } = FinancingStore;
    const vehicle = normalizeRateObject(car);
    const rate = _get(vehicle, 'rate');
    const currentSalesPrice = _get(vehicle, 'price.currentSalesPrice', 0);
    const carId = _get(vehicle, '_id', _get(vehicle, 'id'));
    // Apply only THIS car's user adjustments — untouched cars keep dealer defaults
    const newConfig = _merge(vehicle.rate, getUserCarAdjustments(carId));
    let financingConfig = newConfig.common ? getComputedFinancing(newConfig) : {};
    if (hasUserConfig(carId) && currentSalesPrice === financingConfig.common?.currentSalesPrice) { // price may change on current user session
        financingConfig = getUserConfig(carId); // get user recently customized financing config
    }
    const details = { ...car };
    return {
        details,
        id: carId,
        price: {
            ...rate,
            ...financingConfig,
            common: financingConfig.common || {},
            financing: financingConfig.financing || {},
            leasing: financingConfig.leasing || rate?.leasing || {},
            buy: financingConfig.buy || {},
            currentSalesPrice,
            vatRate: _get(vehicle, 'price.vatRate', null)
        },
        title: {
            make: car.mainData.make && car.mainData.make.replace(/^\w/, (char) => char.toUpperCase()),
            model: car.mainData.model && car.mainData.model.replace(/^\w/, (char) => char.toUpperCase()),
            subModel: car.mainData.subModel,
            power: `${car.engineData.powerKW ? car.engineData.powerKW : ''} (${car.engineData.powerPS ? car.engineData.powerPS : ''}) kW (PS)`
        },
        mainImageId: _get(car, 'metaData.mainImageId', null)
    };
};
export const transformManufacturers = (brands) => {
    const { top = [], regular = [] } = brands;
    const brandsArr = [];
    if (top.length) {
        brandsArr.push({ value: 'top', label: i18nextInstance.t('common:topMark'), isDisabled: true });
        top.map((brand) => brandsArr.push({ value: brand.title, label: brand.title }));
    }
    if (regular.length) {
        brandsArr.push({ value: 'regular', label: i18nextInstance.t('common:regularMark'), isDisabled: true });
        regular.map((brand) => brandsArr.push({ value: brand.title, label: brand.title }));
    }
    return brandsArr;
};
export const transformManufacturersMobx = (manufacturers) => {
    const { top = [], regular = [] } = manufacturers;
    const manufacturersArr = [];
    if (top.length) {
        manufacturersArr.push({ value: 'top', label: i18nextInstance.t('common:topMark'), isDisabled: true });
        top.map((make) => manufacturersArr.push({ value: make.id, label: make.title }));
    }
    if (regular.length) {
        manufacturersArr.push({ value: 'regular', label: i18nextInstance.t('common:regularMark'), isDisabled: true });
        regular.map((make) => manufacturersArr.push({ value: make.id, label: make.title }));
    }
    return manufacturersArr;
};
// ADV-2xx: "Sonstige" (catch-all model) and "default" (the synthetic series title the
// mapping service fabricates for makers with no real series data — see
// car-makemodel-mapping/information.service.ts's `buildSeries`) are placeholder rows, not
// real models. Customer-facing search dropdowns (HP/Landing/SRL/Detailsuche) hide both;
// admin/dealer contexts (VehicleEditPage's own model picker) still need to be able to pick
// them for cars that genuinely have no better match, so filtering is opt-in via the 2nd arg
// — every existing caller that doesn't pass it keeps the unfiltered list.
// Casing is inconsistent across makers' real catalog data (confirmed live: MG's "Sonstige"
// model actually comes back as "SONSTIGE") — compare case-insensitively, not by exact string.
const HIDDEN_MODEL_TITLES = ['sonstige', 'default'];
const isHiddenModelTitle = (title) => HIDDEN_MODEL_TITLES.indexOf(String(title).toLowerCase()) !== -1;
export const transformModelsGeneral = (models, hideSpecialModels) => {
    const groupArr = [];
    Object.keys(models).map((key) => {
        if (!hideSpecialModels || !isHiddenModelTitle(models[key].title)) {
            groupArr.push({
                value: models[key].title,
                label: models[key].title,
                isSeries: true
            });
        }
        models[key].models
            .filter((model) => !hideSpecialModels || !isHiddenModelTitle(model.title))
            .map((model) => groupArr.push({
            value: model.title,
            label: model.title
        }));
    });
    return groupArr;
};
export const transformModels = (models) => {
    const groupArr = [];
    Object.keys(models).map((key) => {
        groupArr.push({
            value: models[key].id,
            label: models[key].title,
            isSeries: true
        });
        models[key].models.map((model) => groupArr.push({
            value: model.id,
            label: model.title
        }));
    });
    return groupArr;
};
export const transformYearsAndMonths = (dates) => {
    const rawLang = i18nextInstance.language || DEFAULT_LANG;
    const lang = MONTHS[rawLang] ? rawLang : 'de';
    const MONTH_COUNT = 12;
    const years = [];
    const months = [];
    for (let i = dates.to_year; i >= dates.from_year; i -= 1) {
        years.push({
            label: i,
            value: i,
            from_year: dates.from_year,
            to_year: dates.to_year
        });
    }
    for (let i = 0; i < MONTH_COUNT; i += 1) {
        months.push({
            label: MONTHS[lang][i],
            value: i + 1,
            from_month: dates.from_month,
            to_month: dates.to_month,
            month: i + 1
        });
    }
    return {
        years,
        months
    };
};
export const transformGeneralGroupedInfo = (data) => {
    const fuel = data.fuel.map((item) => ({ label: item, value: item }));
    const doors = data.doors.map((item) => ({ label: item, value: item }));
    const vehicleTypeName = data.vehicle_type_name.map((item) => ({ label: item, value: item }));
    const series = data.subModels.map((item) => ({ label: item.name, ...item, value: item.id }));
    return {
        fuel,
        doors,
        vehicleTypeName,
        series
    };
};
export const transformDealerUserList = (list) => list.map((item) => {
    const { user, roles } = item;
    const firstName = user.userAddress.firstName || '-';
    const lastName = user.userAddress.lastName || '-';
    const isAdmin = roles.length && roles.map((role) => role.code === DEALER_ADMIN);
    return ({
        isAdmin,
        id: user.id || '',
        name: `${firstName} ${lastName}`,
        company: user.userAddress.company || '',
        contacts: {
            gender: _get(user, 'userAddress.gender', 0),
            email: user.email || '-',
            phoneMain: user.userAddress.phoneMain || '-',
            phoneMobile: _get(user, 'userAddress.phoneMobile', '-'),
            fax: _get(user, 'userAddress.fax', '-')
        }
    });
});
// ADV-205: the SRP card path bypasses FinancingStore.normalizeRateObject, so the
// filter's Laufzeit/Anzahlung (financingStore.searchConfig) never reached the cards and
// every default-config car rendered its 48-month fallback rate regardless of the selected
// duration. Stamp them onto the raw rate before the base normalizeRateObject runs.
// A duration missing from this car's own rate table is NOT stamped — the base function
// would silently snap it to a 24-month fallback, which is worse than the car's own term.
const applySearchRateConfig = (rawData) => {
    const rate = rawData && rawData.rate;
    if (!rate)
        return;
    const searchConfig = FinancingStore.searchConfig || {};
    const paybackPeriod = Number(_get(searchConfig, 'paybackPeriod'));
    if (Number.isFinite(paybackPeriod) && paybackPeriod > 0) {
        const table = rate.periodInterestRate;
        const hasDuration = !Array.isArray(table) || table.some((row) => row && row.duration === paybackPeriod);
        if (hasDuration)
            rate.paybackPeriod = paybackPeriod;
    }
    const firstInstallment = Number(_get(searchConfig, 'firstInstallment'));
    if (Number.isFinite(firstInstallment) && firstInstallment > 0) {
        rate.firstInstallment = firstInstallment;
    }
};
export const transformDataForVehicles = (car, t, seoTextShow = false) => {
    const { getUserCarAdjustments, getUserConfig, hasUserConfig } = FinancingStore;
    // Capture raw firstInstallment before normalizeRateObject strips null values
    const rawData = _get(car, 'data') ? car.data : car;
    const rawRateFirstInstallment = _get(rawData, 'rate.firstInstallment');
    const rawLeasingFirstInstallment = _get(rawData, 'leasing.firstInstallment');
    applySearchRateConfig(rawData);
    const vehicle = _get(car, 'data') ? normalizeRateObject(car.data) : normalizeRateObject(car);
    const rate = _get(vehicle, 'rate');
    const currentSalesPrice = _get(vehicle, 'price.currentSalesPrice', 0);
    const carId = _get(vehicle, '_id', _get(vehicle, 'id'));
    // Apply only THIS car's user adjustments — untouched cars keep dealer defaults
    const carAdjustments = getUserCarAdjustments(carId);
    const newConfig = _merge(vehicle.rate, carAdjustments);
    let financingConfig = newConfig.common ? getComputedFinancing(newConfig) : {};
    if (hasUserConfig(carId) && currentSalesPrice === financingConfig.common?.currentSalesPrice) { // price may change on current user session
        financingConfig = getUserConfig(carId); // get user recently customized financing config
    }
    // A user-configured down payment (financing slider → per-car adjustments in
    // financing_store) overrides the dealer's 0-€-Anzahlung promo, but only for
    // the car the user actually adjusted: a non-zero value on either offer type
    // blocks the badge for this car alone.
    const userFinancingFirstInstallment = _get(carAdjustments, 'financing.firstInstallment');
    const userLeasingFirstInstallment = _get(carAdjustments, 'leasing.firstInstallment');
    const userHasNonZeroFirstInstallment = (userFinancingFirstInstallment != null && userFinancingFirstInstallment !== 0) ||
        (userLeasingFirstInstallment != null && userLeasingFirstInstallment !== 0);
    const hasZeroFirstInstallment = !userHasNonZeroFirstInstallment
        && (rawRateFirstInstallment ?? 0) === 0 && (rawLeasingFirstInstallment ?? 0) === 0;
    // offerSource
    const offerSource = _get(vehicle, 'offer.offerSource', '');
    // metaData
    const metaData = _get(vehicle, 'metaData'); // TODO: remove if not valid?
    const createdAt = _get(metaData, 'createdAt');
    const parkedFor = _get(metaData, 'price.currentSalesPrice', 'N/A');
    const lastCrawledAt = _get(metaData, 'lastCrawledAt', '');
    const ownerName = _get(metaData, 'ownerName', '');
    const vehicleId = _get(metaData, 'vehicleId', null);
    const modificationDate = _get(metaData, 'modificationDate');
    const mileage = _get(vehicle, 'mainData.mileage', 0);
    const firstRegistration = _get(vehicle, 'mainData.firstRegistration', 0);
    const itemSeo = [];
    const typeAlternative = _get(car, 'metaData.type');
    const powerKW = _get(vehicle, 'engineData.powerKW');
    const powerPS = _get(vehicle, 'engineData.powerPS');
    if (seoTextShow) {
        const seoFields = [
            'consumption.fuel',
            'mainData.category',
            'driveSuspension.gearbox',
            'exterior.color',
            'mainData.usageType',
            'mainData.condition',
            'driveSuspension.driveType'
        ];
        seoFields.forEach((field) => {
            const property = _get(vehicle, field);
            if (!property)
                return;
            itemSeo.push(t(`cbd:${property}`));
        });
    }
    return ({
        ...createdAt ? { createdAt } : null,
        parkedFor,
        typeAlternative,
        id: carId,
        vehicleId,
        mainImageId: _get(vehicle, 'metaData.mainImageId'),
        imagesCount: _get(vehicle, 'metaData.imagesCount', 0),
        hasInteriorExteriorPhoto: _get(metaData, 'imagesByApp', _get(metaData, 'imageView360', false)),
        title: {
            make: _get(vehicle, 'mainData.make', ''),
            model: _get(vehicle, 'mainData.model', ''),
            subModel: _get(vehicle, 'mainData.subModel', ''),
            option: _get(vehicle, 'mainData.option', ''),
            to: _get(vehicle, 'title.option', '')
        },
        showNewLabel: _get(vehicle, 'showNewLabel', shouldShowNewLabel(Number(modificationDate), Number(mileage), Number(firstRegistration))),
        // Equipment-highlight chips for the SRP card (cbd keys, most marketing-relevant first).
        // The formatted search car carries the equipment as PER-CATEGORY boolean objects
        // (lights/assistance/multimedia/heatingCooling/window/seats/exterior/driveSuspension), populated
        // by car-search-endpoint's ResultsConverter from the ES `filter_tags`/`lang_neutral` — real,
        // shape-stable booleans (NOT the raw mongo equipment arrays that broke fe-ui-core's
        // getActualHighlights). Plus the pre-computed grouped `search.*` flags. We read defensively and
        // MUST never throw: this runs inside SearchResultsStore's per-car try-loop — a throw = 0 cards.
        highlights: (() => {
            const s = _get(vehicle, 'search', {}) || {};
            const lights = _get(vehicle, 'lights', {}) || {};
            const asst = _get(vehicle, 'assistance', {}) || {};
            const mm = _get(vehicle, 'multimedia', {}) || {};
            const hc = _get(vehicle, 'heatingCooling', {}) || {};
            const win = _get(vehicle, 'window', {}) || {};
            const seats = _get(vehicle, 'seats', {}) || {};
            const ext = _get(vehicle, 'exterior', {}) || {};
            const drive = _get(vehicle, 'driveSuspension', {}) || {};
            const clim = String(hc.climatisation || '');
            const driveType = String(drive.driveType || '');
            // ordered candidate list — [predicate, cbd translation key]; first matches win (sliced
            // downstream). Keys resolve through t('cbd:…') in toAutobahnCar so the chips translate
            // per language (all keys exist in i18n-dictionaries en+de cbd.json).
            const candidates = [
                [!!mm.navigationSystem, 'multimedia_navigationSystem'],
                [!!(lights.ledHeadlights || lights.laserHeadlights || s.noIncandescentLight), 'lights_ledHeadlights'],
                [!!(lights.xenonHeadlights && !lights.ledHeadlights && !lights.laserHeadlights), 'lights_xenonHeadlights'],
                [!!win.panoramicGlassRoof, 'window_panoramicGlassRoof'],
                [!!asst.headUpDisplay, 'assistants_headUpDisplay'],
                [!!asst.adaptiveCruiseControl, 'assistants_adaptiveCruiseControl'],
                [!!(s.seatHeating || seats.electricHeated), 'search_seatHeating'],
                [/automatic/i.test(clim), 'selector_climatisation_automaticAirCondition'],
                [!!clim && !/automatic/i.test(clim), 'selector_climatisation_airCondition'],
                [!!(s.parkingAssistants || asst.parkingAssistants), 'search_parkingAssistants'],
                [/allWheel/i.test(driveType), 'selector_driveType_allWheelDrive'],
                [!!s.trailerHitch, 'search_trailerHitch'],
                [!!(asst.cruiseControl && !asst.adaptiveCruiseControl), 'assistants_cruiseControl'],
                [!!asst.trafficSignRecognition, 'assistants_trafficSignRecognition'],
                [!!asst.collisionAvoidance, 'assistants_collisionAvoidance'],
                [!!win.sunroof && !win.panoramicGlassRoof, 'window_sunroof'],
                [!!seats.electricAdjustable, 'seats_electricAdjustable'],
                [!!ext.metallic, 'exterior_metallic'],
                [!!mm.handsFreePhoneSystem, 'multimedia_handsFreePhoneSystem'],
                [!!hc.auxiliaryHeating, 'heatingCooling_auxiliaryHeating'],
                [!!mm.bluetooth, 'multimedia_bluetooth'],
            ];
            return candidates.filter(([on]) => on).map(([, label]) => label);
        })(),
        info: {
            regDate: firstRegistration,
            mileage,
            gearbox: _get(vehicle, 'driveSuspension.gearbox', 'N/A'),
            numberOfPreviousOwners: _get(vehicle, 'mainData.numberOfPreviousOwners', 'N/A'),
            usageType: _get(vehicle, 'mainData.usageType', 'N/A'),
            condition: _get(vehicle, 'mainData.condition', 'N/A'),
            damaged: _get(vehicle, 'mainData.damaged', 'N/A'),
            driveType: _get(vehicle, 'driveSuspension.driveType', 'N/A')
        },
        location: {
            city: _get(vehicle, 'location.city', 'N/A'),
            street: _get(vehicle, 'location.street', 'N/A'),
            zipCode: _get(vehicle, 'location.zipCode', 0)
        },
        consumption: {
            fuel: _get(vehicle, 'consumption.fuel', _get(vehicle, 'mainData.fuelType', 'N/A')),
            consumptionCombined: _get(vehicle, 'consumption.consumptionCombined', 'N/A'),
            wltpCombined: _get(vehicle, 'consumption.wltpCombined', null),
            consumptionPowerCombined: _get(vehicle, 'consumption.consumptionPowerCombined', 'N/A'),
            wltpPowerCombined: _get(vehicle, 'consumption.wltpPowerCombined', null),
            wltpWeightedPowerCombined: _get(vehicle, 'consumption.wltpWeightedPowerCombined', null),
            wltpWeightedCombined: _get(vehicle, 'consumption.wltpWeightedCombined', null)
        },
        engineData: {
            powerKW: _isNumber(powerKW) ? Math.floor(powerKW) : parseInt(powerKW, 10),
            powerPS: _isNumber(powerPS) ? Math.floor(powerPS) : parseInt(powerPS, 10),
            hybridPlugin: _get(vehicle, 'engineData.hybridPlugin', null)
        },
        environmentEmissions: {
            co2: _get(vehicle, 'environmentEmissions.co2', 'N/A'),
            wltpCo2: _get(vehicle, 'environmentEmissions.wltpCo2', null),
            wltpEnergyEfficiencyClass: _get(vehicle, 'environmentEmissions.wltpEnergyEfficiencyClass', null),
            // PHEV "entladen" (discharged-battery) CO2 class — toAutobahnCar's buildPhev() reads this
            // for the EcoLine dual-figure line; was dropped here, so every plug-in hybrid in any list
            // built via transformDataForVehicles (similar cars, SRP, favorites, …) silently fell back
            // to the weighted class instead of its real discharged one.
            wltpDischargedEnergyEfficiencyClass: _get(vehicle, 'environmentEmissions.wltpDischargedEnergyEfficiencyClass', null),
        },
        offer: (() => {
            const rawOffer = _get(vehicle, 'offer', 'N/A');
            if (!rawOffer || rawOffer === 'N/A')
                return rawOffer;
            return { ...rawOffer, showDownPayment: !!rawOffer.showDownPayment && hasZeroFirstInstallment };
        })(),
        price: {
            ...rate,
            ...financingConfig,
            common: financingConfig.common || {},
            financing: rawRateFirstInstallment === 0
                && (userFinancingFirstInstallment == null || userFinancingFirstInstallment === 0)
                && financingConfig.financing
                ? { ...financingConfig.financing, firstInstallment: 0 }
                : financingConfig.financing || {},
            leasing: rawLeasingFirstInstallment === 0
                && (userLeasingFirstInstallment == null || userLeasingFirstInstallment === 0)
                && (financingConfig.leasing || rate?.leasing)
                ? { ...(financingConfig.leasing || rate?.leasing), firstInstallment: 0 }
                : financingConfig.leasing || rate?.leasing || {},
            buy: financingConfig.buy || {},
            currentSalesPrice,
            vatRate: _get(vehicle, 'price.vatRate', null),
            mlCurrentSalesPricePredicted: _get(vehicle, 'price.mlCurrentSalesPricePredicted', 0),
            historyPriceDifference: _get(vehicle, 'price.historyPriceDifference', 0),
            historyPriceDifferencePerCent: _get(vehicle, 'price.historyPriceDifferencePerCent', 0),
            margin: _get(vehicle, 'price.margin', 0),
            toRound: false
        },
        internalNumber: _get(vehicle, 'offer.internalNumber', ''),
        vin: _get(vehicle, 'metaData.vin', ''),
        isOnline: _get(vehicle, 'state.isOnline', ''),
        firstIsOnline: _get(vehicle, 'state.firstIsOnline'),
        url: `/${typeAlternative || 'search'}/vehicle/${carId}`, // alternative || search
        relativeScore: _get(vehicle, 'relativeScore', 0),
        baseUrl: _get(vehicle, 'dealer.baseUrl', ''),
        offerSource: offerSource ? offerSource : '',
        crawledAt: lastCrawledAt ? formatTimestamp({ timestamp: lastCrawledAt, format: 'DD.MM.YYYY' }) : '',
        ownerName: ownerName ? ownerName : '',
        modificationDate: modificationDate ? formatTimestamp({ timestamp: modificationDate, format: 'DD.MM.YYYY' }) : '',
        seoText: seoTextShow ? [
            ...itemSeo
        ].join(', ') : ''
    });
};
// Returns formated number
export const getFormattedNumber = (num) => {
    const lang = i18nextInstance.language || DEFAULT_LANG;
    return lang === 'de' ? num.toLocaleString('de-DE') : num.toLocaleString('en-IN');
};
// Returns relevant Highlights for car item
export const transformRateObject = (rateObj, config = {}) => {
    const { prefix = '', transformers = {} } = config;
    return Object.entries(rateObj).reduce((acc, [key, value]) => {
        const val = Array.isArray(value)
            ? value.reduce((o, v) => ({ ...o, [v.duration]: transformRateObject(v, { ...config, prefix: '' }) }), {})
            : (key in transformers) ? transformers[key](value) : value;
        return { ...acc, [`${prefix}${key}`]: val };
    }, {}); // eslint-disable-line
};
export const transformQuotes = (quotes) => quotes.map(q => ({
    id: _get(q, 'id', Date.now().toString()),
    image: _get(q, 'image', ''),
    name: _get(q, 'name', ''),
    source: 'Kundenbewertung via Google',
    review: _get(q, 'quote_of_customer', '')
})).sort(() => Math.round(Math.random()) - 0.5);
